import { useEffect } from 'react';
import type { FileItem } from '../../../types';

export const useFileSelection = (
  blobFiles: any,
  workflowId: string | null | undefined,
  clientId: string,
  setIsLoadingFiles: (loading: boolean) => void,
  setUsingMockData: (usingMock: boolean) => void,
  setTopicFrameworkFiles: (files: any[]) => void,
  setRelevantKeywordsFiles: (files: any[]) => void,
  setSelectedTopicFrameworkFile: (file: string) => void,
  setSelectedRelevantKeywordsFile: (file: string) => void
) => {
  // Effect to populate file options
  useEffect(() => {
    if (blobFiles && workflowId && clientId) {
      setIsLoadingFiles(true);
      try {
        // Get files from blobFiles and track if they're mock data
        let files = blobFiles.files || [];
        setUsingMockData(blobFiles.isMockData || false);
        
        // If files array is empty (possibly due to API error), populate with hardcoded fallback paths
        if (files.length === 0) {
          console.log('No files found - using hardcoded fallback paths');
          // Create fallback files based on the current workflowId and clientId
          files = [
            `${clientId}/workflows/${workflowId}/module1/topics.json`,
            `${clientId}/workflows/${workflowId}/module2/executions/latest/relevant.csv`,
            `${clientId}/workflows/${workflowId}/module2/output/framework.json`,
          ];
          setUsingMockData(true);
        }
        
        // Module paths
        const module1Path = `${clientId}/workflows/${workflowId}/module1`;
        const module2ExecutionsPath = `${clientId}/workflows/${workflowId}/module2/executions`;
        const module2OutputPath = `${clientId}/workflows/${workflowId}/module2/output`;
        
        // Log path information for debugging
        console.log('Debug - IDs and paths:', {
          clientId,
          workflowId,
          clientIdType: typeof clientId,
          workflowIdType: typeof workflowId,
          module1Path,
          module2OutputPath
        });
        
        // Log all available files for debugging
        console.log('Debug - All available files:', files);
        
        // Get topic framework files from Module 1 and Module 2 directories
        const frameworkFiles = files
          .filter((file: FileItem) => {
            // Handle both string and object paths
            const path = typeof file === 'string' ? file : (file.path || file.name || '');
            
            // Check if this is a JSON file
            const isJsonFile = path.endsWith('.json');
            if (!isJsonFile) return false;
            
            // Try exact path matching first
            if (path.startsWith(module1Path) || 
                path.startsWith(module2ExecutionsPath) || 
                path.startsWith(module2OutputPath)) {
              return true;
            }
            
            // More flexible matching using regex pattern for workflow/module paths
            const pattern = new RegExp(`\\/?${clientId}\\/?workflows\\/?${workflowId}\\/?module[12]`);
            return pattern.test(path);
          })
          .map((file: FileItem) => {
            // Handle both string and object paths
            const path = typeof file === 'string' ? file : (file.path || file.name || '');
            const fileName = path.split('/').pop() || path;
            return {
              label: fileName,
              value: path,
              path: path,
              isSample: fileName.toLowerCase().includes('sample')
            };
          });
        
        // Log found framework files
        console.log('Debug - Found framework files:', frameworkFiles);
        
        setTopicFrameworkFiles(frameworkFiles);
        
        // Get Module 2 relevant keywords files
        const keywordFiles = files
          .filter((file: FileItem) => {
            // Handle both string and object paths
            const path = typeof file === 'string' ? file : (file.path || file.name || '');
            
            // Check if this is a CSV file
            const isCsvFile = path.endsWith('.csv');
            if (!isCsvFile) return false;
            
            // Try exact path matching first
            if (path.startsWith(module2ExecutionsPath) || 
                path.startsWith(module2OutputPath)) {
              return true;
            }
            
            // More flexible matching for module2 paths
            const pattern = new RegExp(`\\/?${clientId}\\/?workflows\\/?${workflowId}\\/?module2`);
            return pattern.test(path);
          })
          .map((file: FileItem) => {
            // Handle both string and object paths
            const path = typeof file === 'string' ? file : (file.path || file.name || '');
            const fileName = path.split('/').pop() || path;
            return {
              label: fileName,
              value: path,
              path: path,
              isSample: fileName.toLowerCase().includes('sample')
            };
          });
          
        setRelevantKeywordsFiles(keywordFiles);
        
        // Set defaults if available - but only if current selection is invalid
        if (frameworkFiles.length > 0) {
          // Check if current selection is valid
          const currentSelection = localStorage.getItem(`module3_${workflowId}_topic_framework_file`) || '';
          const isCurrentSelectionValid = frameworkFiles.some(file => file.path === currentSelection);
          
          if (isCurrentSelectionValid) {
            // Restore valid selection from localStorage
            setSelectedTopicFrameworkFile(currentSelection);
          } else {
            // Prioritize finding files in this order
            const topicsFile = frameworkFiles.find(file => 
              file.label === 'topics.json' && !file.isSample && file.path.includes('module1')
            );
            const anyTopicFile = frameworkFiles.find(file => 
              file.label.toLowerCase().includes('topic') && !file.isSample
            );
            const defaultPath = topicsFile ? topicsFile.path : 
                               anyTopicFile ? anyTopicFile.path : 
                               frameworkFiles[0].path;
            setSelectedTopicFrameworkFile(defaultPath);
          }
        } else {
          // Clear selection if no files available
          setSelectedTopicFrameworkFile('');
        }
        
        if (keywordFiles.length > 0) {
          // Check if current selection is valid
          const currentSelection = localStorage.getItem(`module3_${workflowId}_relevant_keywords_file`) || '';
          const isCurrentSelectionValid = keywordFiles.some(file => file.path === currentSelection);
          
          if (isCurrentSelectionValid) {
            // Restore valid selection from localStorage
            setSelectedRelevantKeywordsFile(currentSelection);
          } else {
            // Prioritize finding relevant.csv files
            const relevantFile = keywordFiles.find(file => 
              file.label === 'relevant.csv' && !file.isSample && file.path.includes('module2')
            );
            const anyRelevantFile = keywordFiles.find(file => 
              file.label.toLowerCase().includes('relevant') && !file.isSample
            );
            const defaultPath = relevantFile ? relevantFile.path : 
                               anyRelevantFile ? anyRelevantFile.path : 
                               keywordFiles[0].path;
            setSelectedRelevantKeywordsFile(defaultPath);
          }
        } else {
          // Clear selection if no files available
          setSelectedRelevantKeywordsFile('');
        }
      } catch (error) {
        console.error('Error processing blob files:', error);
      } finally {
        setIsLoadingFiles(false);
      }
    }
  }, [blobFiles, workflowId, clientId, setIsLoadingFiles, setUsingMockData, setTopicFrameworkFiles, setRelevantKeywordsFiles, setSelectedTopicFrameworkFile, setSelectedRelevantKeywordsFile]);
};

export default useFileSelection;
